import axios from 'axios';
import { AuthResponse, LoginData, Freevening, CreateFreeveningData, OverlapData, NotificationPreferences, UpdateNotificationPreferencesData } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const USE_COGNITO = process.env.REACT_APP_USE_COGNITO === 'true';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Use same token key as Cognito for consistency
  const token = localStorage.getItem('cognito_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login endpoints - let the form handle the error
      const requestUrl = error.config?.url || '';
      const authEndpoints = USE_COGNITO 
        ? ['/cognito-auth/login', '/auth/login'] // Include fallback endpoint
        : ['/auth/login'];
      const isAuthEndpoint = authEndpoints.some(endpoint => requestUrl.includes(endpoint));
      
      if (!isAuthEndpoint) {
        // Clean up all auth tokens on 401 errors
        localStorage.removeItem('auth_token');
        localStorage.removeItem('cognito_access_token');
        localStorage.removeItem('cognito_refresh_token');
        localStorage.removeItem('cognito_id_token');
        localStorage.removeItem('user');
        // Force a full page reload to index.html which will handle routing
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (data: LoginData): Promise<AuthResponse> => {
    let endpoint = USE_COGNITO ? '/cognito-auth/login' : '/auth/login';
    
    try {
      const response = await api.post(endpoint, data);
      return response.data;
    } catch (error: any) {
      // Fallback: if Cognito endpoint doesn't exist (404), try legacy endpoint
      if (USE_COGNITO && error.response?.status === 404) {
        console.warn('Cognito auth endpoint not found, falling back to legacy auth');
        const fallbackResponse = await api.post('/auth/login', data);
        return fallbackResponse.data;
      }
      throw error;
    }
  },


  getCurrentUser: async () => {
    let endpoint = USE_COGNITO ? '/cognito-auth/me' : '/auth/me';
    
    try {
      const response = await api.get(endpoint);
      return response.data;
    } catch (error: any) {
      // Fallback: if Cognito endpoint doesn't exist (404), try legacy endpoint
      if (USE_COGNITO && error.response?.status === 404) {
        console.warn('Cognito auth endpoint not found, falling back to legacy auth');
        const fallbackResponse = await api.get('/auth/me');
        return fallbackResponse.data;
      }
      throw error;
    }
  },

  completePasswordChallenge: async (data: { email: string; tempPassword: string; newPassword: string }): Promise<AuthResponse> => {
    if (!USE_COGNITO) {
      throw new Error('Password challenge completion only available with Cognito');
    }
    const response = await api.post('/cognito-auth/complete-password-challenge', data);
    return response.data;
  },
};

export const freeveningApi = {
  getFreevenings: async (startDate?: string, endDate?: string): Promise<Freevening[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/freevenings?${params}`);
    return response.data;
  },

  createFreevening: async (data: CreateFreeveningData): Promise<{ message: string; freevening: Freevening }> => {
    const response = await api.post('/freevenings', data);
    return response.data;
  },

  updateFreevening: async (id: string, data: Partial<CreateFreeveningData>): Promise<{ message: string; freevening: Freevening }> => {
    const response = await api.put(`/freevenings/${id}`, data);
    return response.data;
  },

  deleteFreevening: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/freevenings/${id}`);
    return response.data;
  },

  deleteFreeveningByDate: async (date: string): Promise<{ message: string }> => {
    const response = await api.delete(`/freevenings/date/${date}`);
    return response.data;
  },

  clearAllFreeveningsForDate: async (date: string): Promise<{ message: string; removed_count: number }> => {
    const response = await api.delete(`/freevenings/admin/clear-date/${date}`);
    return response.data;
  },

  getOverlaps: async (startDate: string, endDate: string): Promise<OverlapData[]> => {
    const response = await api.get(`/freevenings/overlaps?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },
};

export const notificationApi = {
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },

  updatePreferences: async (data: UpdateNotificationPreferencesData): Promise<{ message: string; email_cadence: string }> => {
    const response = await api.put('/notifications/preferences', data);
    return response.data;
  },

  testNotification: async (testDate: string): Promise<{ message: string }> => {
    const response = await api.post('/notifications/test', { test_date: testDate });
    return response.data;
  },
};

export default api;